import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormInput,
  FormPrice,
  FormRoot,
  RHF,
  zodResolver,
} from "@devhop/ui";
import { format } from "date-fns";
import { PencilIcon, PlusIcon, TrashIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { z } from "zod";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import {
  installmentPlanTargetAmount,
  isLegacyTaxInclusiveInstallmentPlan,
  resolveInstallmentPrintAmountExTax,
} from "@/shared/lib/performance-document-payment-terms";
import {
  DOCUMENT_CURRENCY_SYMBOL,
  type DocumentCurrency,
  formatDocumentMoney,
  toMajorUnits,
  toMinorUnits,
} from "@/shared/lib/performance-document-currency";
import type { DocumentDTO, InstallmentDTO } from "../../domain/contracts";
import { buildInstallmentsFromTemplate } from "../../domain/services/installment-plan-templates";
import {
  useCreateDocumentFromParent,
  useInstallmentPlanTemplatesLookup,
  useQuotationInstallmentPlan,
  useUpdateQuotationInstallmentPlan,
} from "../api/queries";
import { DocumentInstallmentLinesTable } from "./DocumentInstallmentLinesTable";

const INSTALLMENT_FORM_SCHEMA = z.object({
  rows: z
    .array(
      z.object({
        key: z.string(),
        installmentNo: z.number().int().positive(),
        label: z.string().max(200).optional(),
        labelEn: z.string().max(200).optional(),
        dueDate: z.string().optional(),
        amount: z.number().positive(),
        locked: z.boolean(),
      }),
    )
    .min(1),
});

type InstallmentFormValues = z.infer<typeof INSTALLMENT_FORM_SCHEMA>;
type InstallmentFormRow = InstallmentFormValues["rows"][number];

function mapQuotationInstallmentsToFormRows(
  installments: InstallmentDTO[],
  planTargetMajor: number,
  currency: DocumentCurrency,
  quotation: DocumentDTO,
): InstallmentFormRow[] {
  if (installments.length === 0) {
    return [
      {
        key: "new-1",
        installmentNo: 1,
        label: "ງວດ 1",
        labelEn: "Installment 1",
        dueDate: "",
        amount: planTargetMajor,
        locked: false,
      },
    ];
  }

  const planSum = installments.reduce((sum, row) => sum + row.amount, 0);
  const legacyInclusive = isLegacyTaxInclusiveInstallmentPlan(planSum, quotation);

  return installments.map((row) => ({
    key: row.id,
    installmentNo: row.installmentNo,
    label: row.label ?? "",
    labelEn: row.labelEn ?? "",
    dueDate: row.dueDate ? format(new Date(row.dueDate), "yyyy-MM-dd") : "",
    amount: legacyInclusive
      ? toMajorUnits(
          resolveInstallmentPrintAmountExTax(row.amount, {
            grandTotal: quotation.grandTotal,
            taxAmount: quotation.taxAmount ?? 0,
            taxEnabled: quotation.taxEnabled ?? false,
          }),
          currency,
        )
      : toMajorUnits(row.amount, currency),
    locked: Boolean(row.invoicedInvoiceId),
  }));
}

function balanceInstallmentRows(
  rows: InstallmentFormRow[],
  planTargetMajor: number,
  currency: DocumentCurrency,
): InstallmentFormRow[] {
  if (rows.length === 0) return rows;

  const decimals = currency === "LAK" ? 0 : 2;
  const editableIndexes = rows
    .map((row, index) => (row.locked ? -1 : index))
    .filter((index) => index >= 0);

  if (editableIndexes.length === 0) return rows;

  const lastEditableIndex = editableIndexes[editableIndexes.length - 1]!;
  const othersTotal = rows.reduce((sum, row, index) => {
    if (index === lastEditableIndex) return sum;
    return sum + (row.amount || 0);
  }, 0);

  const nextAmount = Number((planTargetMajor - othersTotal).toFixed(decimals));

  return rows.map((row, index) =>
    index === lastEditableIndex
      ? { ...row, amount: Math.max(nextAmount, currency === "LAK" ? 1 : 0.01) }
      : row,
  );
}

function formatMoney(value: number, currency: DocumentCurrency) {
  return formatDocumentMoney(value, currency);
}

function buildInstallmentPlanInputFromTemplate(
  template: Parameters<typeof buildInstallmentsFromTemplate>[0],
  quotation: DocumentDTO,
  currency: DocumentCurrency,
  baseDate: Date,
) {
  const planTargetMajor = toMajorUnits(
    installmentPlanTargetAmount(quotation),
    currency,
  );
  return buildInstallmentsFromTemplate(template, planTargetMajor, baseDate).map(
    (row) => ({
      installmentNo: row.installmentNo,
      label: row.label,
      labelEn: row.labelEn,
      dueDate: row.dueDate,
      amount: row.amount,
    }),
  );
}

export function QuotationInstallmentPlanPanel({
  eventId,
  quotation,
  onInvoiceCreated,
}: {
  eventId: string;
  quotation: DocumentDTO;
  onInvoiceCreated?: (documentId: string) => void;
}) {
  const canEdit = useActionPermission(["performance-documents:create"]);
  const [editOpen, setEditOpen] = useState(false);
  const currency = quotation.currency ?? "LAK";
  const planQuery = useQuotationInstallmentPlan(eventId, quotation.id);
  const updatePlan = useUpdateQuotationInstallmentPlan(eventId, quotation.id);
  const createInvoice = useCreateDocumentFromParent(eventId);
  const templatesLookup = useInstallmentPlanTemplatesLookup();
  const defaultTemplate =
    templatesLookup.data?.items.find((t) => t.isDefault) ??
    templatesLookup.data?.items[0];
  const installments = planQuery.data?.installments ?? [];
  const hasPlan = installments.length > 0;
  const canCreateInvoice = canEdit && quotation.status === "issued";
  const planTargetMinor = installmentPlanTargetAmount(quotation);
  const planSum = installments.reduce((sum, row) => sum + row.amount, 0);
  const legacyInclusive = isLegacyTaxInclusiveInstallmentPlan(planSum, quotation);

  const displayInstallments = installments.map((row) => ({
    ...row,
    amount: legacyInclusive
      ? resolveInstallmentPrintAmountExTax(row.amount, {
          grandTotal: quotation.grandTotal,
          taxAmount: quotation.taxAmount ?? 0,
          taxEnabled: quotation.taxEnabled ?? false,
        })
      : row.amount,
    status: row.invoicedInvoiceId ? ("paid" as const) : row.status,
  }));

  function handleCreatePlanFromTemplate() {
    if (!defaultTemplate) return;
    const baseDate = quotation.issuedAt
      ? new Date(quotation.issuedAt)
      : new Date();
    updatePlan.mutate({
      installments: buildInstallmentPlanInputFromTemplate(
        defaultTemplate,
        quotation,
        currency,
        baseDate,
      ),
    });
  }

  function handleCreateInvoice(installmentId: string) {
    createInvoice.mutate(
      {
        parentDocumentId: quotation.id,
        documentType: "invoice",
        quotationInstallmentId: installmentId,
      },
      {
        onSuccess: (document) => onInvoiceCreated?.(document.id),
      },
    );
  }

  return (
    <div className="space-y-3 rounded-lg border bg-muted/10 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="font-semibold text-sm">ແຜນງວດຊຳລະ</p>
          <p className="text-muted-foreground text-xs">
            {hasPlan
              ? quotation.taxEnabled
                ? "ກຳນົດງວດໃນໃບສະເໜີລາຄາ — ຈຳນວນເງິນບໍ່ລວມ VAT"
                : "ກຳນົດງວດໃນໃບສະເໜີລາຄາ — ສ້າງໃບເກັບເງິນທີລະງວດ"
              : defaultTemplate
                ? `ຍັງບໍ່ມີແຜນງວດ — ກົດສ້າງເພື່ອໂຫຼດຈາກແມ່ແບບ «${defaultTemplate.name}»`
                : "ຍັງບໍ່ມີແມ່ແບບງວດ — ຕັ້ງແມ່ແບບກ່ອນ"}
          </p>
        </div>
        {canEdit && quotation.status !== "voided" ? (
          hasPlan ? (
            <Button size="sm" variant="outline" onClick={() => setEditOpen(true)}>
              <PencilIcon className="size-4" />
              ແກ້ໄຂແຜນງວດ
            </Button>
          ) : (
            <Button
              size="sm"
              variant="outline"
              disabled={
                updatePlan.isPending ||
                templatesLookup.isLoading ||
                !defaultTemplate
              }
              onClick={handleCreatePlanFromTemplate}
            >
              <PlusIcon className="size-4" />
              {updatePlan.isPending ? "ກຳລັງສ້າງ..." : "ສ້າງແຜນງວດ"}
            </Button>
          )
        ) : null}
      </div>

      {planQuery.isLoading ? (
        <p className="text-muted-foreground text-sm">ກຳລັງໂຫຼດແຜນງວດ...</p>
      ) : (
        <DocumentInstallmentLinesTable
          installments={displayInstallments}
          currency={currency}
          showStatus
          totalLabel={
            quotation.taxEnabled ? "ຍອດລວມແຜນງວດ (ບໍ່ລວມ VAT)" : "ຍອດລວມແຜນງວດ"
          }
          planTargetAmount={planTargetMinor}
          invoiceActions={
            hasPlan
              ? {
                  canCreate: canCreateInvoice,
                  creatingInstallmentId: createInvoice.isPending
                    ? (createInvoice.variables?.quotationInstallmentId ?? null)
                    : null,
                  onCreate: (row) => handleCreateInvoice(row.id),
                  onOpen: onInvoiceCreated,
                }
              : undefined
          }
        />
      )}

      {installments.some((row) => row.invoicedInvoiceId) ? (
        <p className="text-muted-foreground text-xs">
          ງວດທີ່ສະຖານະ «ຊຳລະແລ້ວ» = ສ້າງໃບເກັບເງິນແລ້ວ — ກົດ «ເບິ່ງ INV» ເພື່ອເປີດເອກະສານ
        </p>
      ) : quotation.status !== "issued" && hasPlan ? (
        <p className="text-muted-foreground text-xs">
          ອອກໃບສະເໜີລາຄາກ່ອນ ຈຶ່ງສາມາດສ້າງໃບເກັບເງິນຈາກແຕ່ລະງວດໄດ້
        </p>
      ) : null}

      <QuotationInstallmentPlanDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        eventId={eventId}
        quotationId={quotation.id}
        quotation={quotation}
        installments={installments}
        currency={currency}
      />
    </div>
  );
}

function QuotationInstallmentPlanDialog({
  open,
  onOpenChange,
  eventId,
  quotationId,
  quotation,
  installments,
  currency,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  eventId: string;
  quotationId: string;
  quotation: DocumentDTO;
  installments: InstallmentDTO[];
  currency: DocumentCurrency;
}) {
  const updatePlan = useUpdateQuotationInstallmentPlan(eventId, quotationId);
  const templatesLookup = useInstallmentPlanTemplatesLookup();
  const defaultTemplate =
    templatesLookup.data?.items.find((t) => t.isDefault) ??
    templatesLookup.data?.items[0];
  const symbol = DOCUMENT_CURRENCY_SYMBOL[currency];
  const planTargetMinor = installmentPlanTargetAmount(quotation);
  const planTargetMajor = toMajorUnits(planTargetMinor, currency);
  const hasInvoicedInstallments = installments.some(
    (row) => row.invoicedInvoiceId,
  );

  const methods = RHF.useForm<InstallmentFormValues>({
    resolver: zodResolver(INSTALLMENT_FORM_SCHEMA),
    defaultValues: {
      rows: mapQuotationInstallmentsToFormRows(
        installments,
        planTargetMajor,
        currency,
        quotation,
      ),
    },
  });

  useEffect(() => {
    if (!open) return;
    methods.reset({
      rows: mapQuotationInstallmentsToFormRows(
        installments,
        planTargetMajor,
        currency,
        quotation,
      ),
    });
  }, [open, currency, planTargetMajor, installments, methods, quotation]);

  const rows = methods.watch("rows");
  const plannedTotal = useMemo(
    () => rows.reduce((sum, row) => sum + (row.amount || 0), 0),
    [rows],
  );
  const amountTolerance = currency === "LAK" ? 1 : 0.01;
  const totalsMatch =
    Math.abs(plannedTotal - planTargetMajor) <= amountTolerance;

  const setRows = (next: InstallmentFormRow[]) => {
    methods.setValue("rows", next, { shouldDirty: true, shouldValidate: true });
  };

  const loadFromDefaultTemplate = () => {
    if (!defaultTemplate || hasInvoicedInstallments) return;
    const built = buildInstallmentPlanInputFromTemplate(
      defaultTemplate,
      quotation,
      currency,
      new Date(),
    );
    setRows(
      built.map((row) => ({
        key: `template-${row.installmentNo}`,
        installmentNo: row.installmentNo,
        label: row.label ?? "",
        labelEn: row.labelEn ?? "",
        dueDate: row.dueDate ? format(row.dueDate, "yyyy-MM-dd") : "",
        amount: row.amount,
        locked: false,
      })),
    );
  };

  const addRow = () => {
    const nextNo =
      rows.reduce((max, row) => Math.max(max, row.installmentNo), 0) + 1;
    const remaining = Number((planTargetMajor - plannedTotal).toFixed(2));
    setRows([
      ...rows,
      {
        key: `new-${nextNo}-${Date.now()}`,
        installmentNo: nextNo,
        label: `ງວດ ${nextNo}`,
        labelEn: `Installment ${nextNo}`,
        dueDate: "",
        amount: remaining > 0 ? remaining : amountTolerance,
        locked: false,
      },
    ]);
  };

  const removeRow = (index: number) => {
    const target = rows[index];
    if (!target || target.locked || rows.length <= 1) return;
    setRows(
      balanceInstallmentRows(
        rows.filter((_, i) => i !== index),
        planTargetMajor,
        currency,
      ),
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>ແຜນງວດຊຳລະ (ໃບສະເໜີລາຄາ)</DialogTitle>
        </DialogHeader>
        <FormRoot
          methods={methods}
          onSubmit={(values) => {
            if (!totalsMatch) return;
            updatePlan.mutate(
              {
                installments: values.rows.map((row) => ({
                  installmentNo: row.installmentNo,
                  label: row.label || undefined,
                  labelEn: row.labelEn || undefined,
                  dueDate: row.dueDate ? new Date(row.dueDate) : null,
                  amount: row.amount,
                })),
              },
              { onSuccess: () => onOpenChange(false) },
            );
          }}
          className="space-y-4"
        >
          <p className="text-muted-foreground text-xs">
            {quotation.taxEnabled
              ? "ຍອດລວມທຸກງວດຕ້ອງເທົ່າຍອດກ່ອນ VAT — ງວດທີ່ສ້າງ INV ແລ້ວແກ້ຍອດບໍ່ໄດ້"
              : "ຍອດລວມທຸກງວດຕ້ອງເທົ່າຍອດໃບສະເໜີລາຄາ — ງວດທີ່ສ້າງໃບເກັບເງິນແລ້ວແກ້ຍອດບໍ່ໄດ້"}
          </p>
          <div className="max-h-[50vh] space-y-3 overflow-y-auto pr-1">
            {rows.map((row, index) => (
              <div key={row.key} className="space-y-2 rounded-lg border p-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-medium text-sm">ງວດ {row.installmentNo}</p>
                    {row.locked ? (
                      <Badge variant="default">ສ້າງ INV ແລ້ວ</Badge>
                    ) : null}
                  </div>
                  {!row.locked && rows.length > 1 ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="text-destructive"
                      onClick={() => removeRow(index)}
                    >
                      <TrashIcon className="size-4" />
                      ລຶບ
                    </Button>
                  ) : null}
                </div>
                <div className="grid gap-2 md:grid-cols-2">
                  <FormInput name={`rows.${index}.label`} label="ປ້າຍ (ລາວ)" />
                  <FormInput name={`rows.${index}.labelEn`} label="Label (EN)" />
                  <FormInput
                    name={`rows.${index}.dueDate`}
                    label="ຄົບກຳນົດ"
                    type="date"
                  />
                  <FormPrice
                    currencySymbol={symbol}
                    name={`rows.${index}.amount`}
                    label="ຍອດ"
                    disabled={row.locked}
                  />
                </div>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
            <span className="text-muted-foreground">
              {quotation.taxEnabled ? "ຍອດກ່ອນ VAT" : "ຍອດໃບສະເໜີລາຄາ"}:{" "}
              {formatMoney(planTargetMinor, currency)}
            </span>
            <span
              className={
                totalsMatch
                  ? "font-medium text-primary"
                  : "font-medium text-destructive"
              }
            >
              ລວມແຜນ:{" "}
              {formatDocumentMoney(
                toMinorUnits(plannedTotal, currency),
                currency,
              )}
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" size="sm" onClick={addRow}>
              <PlusIcon className="size-4" />
              ເພີ່ມງວດ
            </Button>
            {defaultTemplate ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={hasInvoicedInstallments}
                onClick={loadFromDefaultTemplate}
              >
                ໂຫຼດແມ່ແບບ
              </Button>
            ) : null}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              ຍົກເລີກ
            </Button>
            <Button type="submit" disabled={updatePlan.isPending || !totalsMatch}>
              {updatePlan.isPending ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກແຜນ"}
            </Button>
          </DialogFooter>
        </FormRoot>
      </DialogContent>
    </Dialog>
  );
}
